/**
 * SupplyPlanFreeDelivery.jsx — Feb 2026 mid-month.
 *
 * Public delivery page for the free supply-plan lead magnet. Renders
 * the generated plan on-screen (markdown) and offers a big "Download
 * PDF" button. Emailed link points here so the teacher can re-open
 * the plan any time in the 30-day window.
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Download, FileText, ArrowRight, Sparkles, Users } from "lucide-react";
import {
  fetchSupplyPlan,
  supplyPlanPdfUrl,
} from "../lib/supplyPlanFreeApi";
import { markdownComponents } from "../components/LessonDocument";
import ShareSupplyPlanModal from "../components/ShareSupplyPlanModal";

const POLL_INTERVAL_MS = 3000;
const POLL_MAX_ATTEMPTS = 40; // ~2 minutes

const SupplyPlanFreeDelivery = () => {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [attempts, setAttempts] = useState(0);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let timer = null;

    const load = async () => {
      try {
        const data = await fetchSupplyPlan(token);
        if (cancelled) return;
        setState({ loading: false, error: null, data });
        if (
          data?.status === "pending" &&
          attempts < POLL_MAX_ATTEMPTS
        ) {
          timer = setTimeout(() => {
            setAttempts((n) => n + 1);
          }, POLL_INTERVAL_MS);
        }
      } catch (err) {
        if (cancelled) return;
        const detail =
          err?.response?.data?.detail || "This link is no longer available.";
        setState({ loading: false, error: detail, data: null });
      }
    };
    load();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [token, attempts]);

  if (state.loading) {
    return (
      <div
        data-testid="supply-plan-delivery-loading"
        className="min-h-screen flex items-center justify-center bg-[#f4f5f7] text-[#4a6984] text-sm font-semibold uppercase tracking-[0.14em]"
      >
        Loading your supply plan…
      </div>
    );
  }

  if (state.error || !state.data) {
    return (
      <div className="min-h-screen bg-[#f4f5f7] flex items-center justify-center px-4">
        <div
          data-testid="supply-plan-delivery-error"
          className="max-w-md w-full bg-white rounded-2xl border border-[#4a6984]/15 p-8 text-center shadow-sm"
        >
          <p className="text-[11px] font-extrabold uppercase tracking-[0.22em] text-[#4a6984]/70">
            Free supply plan
          </p>
          <h1 className="mt-3 font-black text-2xl text-[#1c2d3d]">
            Link unavailable
          </h1>
          <p className="mt-3 text-sm text-[#4a6984] leading-relaxed">
            {state.error}
          </p>
          <Link
            to="/supply-plans"
            data-testid="supply-plan-delivery-error-back"
            className="mt-6 inline-flex items-center gap-1.5 rounded-md bg-[#1c2d3d] px-5 py-2.5 text-xs font-extrabold uppercase tracking-[0.14em] text-white hover:bg-[#132030]"
          >
            Build another plan
          </Link>
        </div>
      </div>
    );
  }

  const d = state.data;

  if (d.status === "pending") {
    return (
      <div className="min-h-screen bg-[#f4f5f7] flex items-center justify-center px-4">
        <div
          data-testid="supply-plan-delivery-pending"
          className="max-w-md w-full bg-white rounded-2xl border border-[#4a6984]/15 p-8 text-center shadow-sm"
        >
          <p className="text-[11px] font-extrabold uppercase tracking-[0.22em] text-[#96ded1]">
            <Sparkles className="inline h-3.5 w-3.5 mr-1 -mt-0.5" />
            Building your plan
          </p>
          <h1 className="mt-3 font-black text-2xl text-[#1c2d3d]">
            Almost there…
          </h1>
          <p className="mt-3 text-sm text-[#4a6984] leading-relaxed">
            We're writing your full-day plan. This takes a minute or two. This
            page will refresh itself when it's ready.
          </p>
        </div>
      </div>
    );
  }

  if (d.status === "failed") {
    return (
      <div className="min-h-screen bg-[#f4f5f7] flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-2xl p-8 text-center shadow-sm">
          <h1 className="font-black text-2xl text-[#1c2d3d]">
            Something went wrong.
          </h1>
          <p className="mt-3 text-sm text-[#4a6984]">
            {d.error || "We couldn't finish the plan."}
          </p>
          <Link
            to="/supply-plans"
            className="mt-5 inline-flex items-center gap-1.5 rounded-md bg-[#1c2d3d] px-5 py-2.5 text-xs font-extrabold uppercase tracking-[0.14em] text-white hover:bg-[#132030]"
          >
            Try again
          </Link>
        </div>
      </div>
    );
  }

  const activity = d.activity || {};
  const lessonBody = activity.lesson_markdown || "";
  const pdfHref = supplyPlanPdfUrl(token);

  return (
    <div className="min-h-screen bg-[#f4f5f7]">
      {/* Hero strip */}
      <div className="bg-[#1c2d3d] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12">
          <p className="text-[11px] font-extrabold uppercase tracking-[0.22em] text-[#96ded1]">
            <Sparkles className="inline h-3.5 w-3.5 mr-1 -mt-0.5" />
            Your supply plan is ready
          </p>
          <h1
            data-testid="supply-plan-delivery-title"
            className="mt-3 font-black text-3xl sm:text-4xl tracking-tight"
          >
            {activity.activity_name || `Supply Day Plan — ${d.grade}`}
          </h1>
          <p className="mt-3 text-sm sm:text-base text-white/80 leading-relaxed max-w-2xl">
            We've also emailed a copy of this plan (PDF) to{" "}
            <span className="font-extrabold text-white">{d.email}</span>. The
            link is good for 30 days — bookmark it or hand the PDF to your
            supply teacher.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <a
              href={pdfHref}
              data-testid="supply-plan-delivery-pdf-btn"
              className="inline-flex items-center justify-center gap-1.5 rounded-md bg-[#96ded1] px-5 py-3 text-xs font-extrabold uppercase tracking-[0.14em] text-[#1c2d3d] hover:bg-white transition-colors"
            >
              <Download className="h-3.5 w-3.5" />
              Download PDF
            </a>
            <button
              type="button"
              onClick={() => window.print()}
              data-testid="supply-plan-delivery-print-btn"
              className="inline-flex items-center justify-center gap-1.5 rounded-md border border-white/25 px-5 py-3 text-xs font-extrabold uppercase tracking-[0.14em] text-white hover:bg-white/10"
            >
              <FileText className="h-3.5 w-3.5" />
              Print this page
            </button>
            <button
              type="button"
              onClick={() => setShareOpen(true)}
              data-testid="supply-plan-delivery-share-btn"
              className="inline-flex items-center justify-center gap-1.5 rounded-md border border-white/25 px-5 py-3 text-xs font-extrabold uppercase tracking-[0.14em] text-white hover:bg-white/10"
            >
              <Users className="h-3.5 w-3.5" />
              Share with a colleague
            </button>
          </div>
        </div>
      </div>

      <ShareSupplyPlanModal
        open={shareOpen}
        onOpenChange={setShareOpen}
        token={token}
        grade={d.grade}
      />

      {/* Rendered plan body */}
      <div
        data-testid="supply-plan-delivery-body"
        className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14"
      >
        <div className="bg-white rounded-2xl border border-[#4a6984]/15 p-6 sm:p-10 shadow-sm">
          <div className="prose prose-slate max-w-none atomic-lesson-body">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={markdownComponents}
            >
              {lessonBody}
            </ReactMarkdown>
          </div>
        </div>

        {/* Soft upsell footer */}
        <div className="mt-10 rounded-2xl bg-[#1c2d3d] text-white p-6 sm:p-8">
          <p className="text-[11px] font-extrabold uppercase tracking-[0.22em] text-[#96ded1]">
            Beyond today
          </p>
          <h2 className="mt-2 font-black text-2xl">
            Atomic Edu builds these for your whole year.
          </h2>
          <p className="mt-3 text-sm text-white/80 max-w-2xl leading-relaxed">
            Unlimited supply plans, full lessons, worksheets with answer keys,
            and report-card language. Seven-day free trial, no card up front.
          </p>
          <Link
            to="/pricing"
            data-testid="supply-plan-delivery-upsell"
            className="mt-5 inline-flex items-center gap-1.5 rounded-md bg-[#96ded1] px-5 py-2.5 text-xs font-extrabold uppercase tracking-[0.14em] text-[#1c2d3d] hover:bg-white"
          >
            See what's included
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>

        <p className="mt-8 text-xs text-[#4a6984]/70">
          Made free with Atomic Edu · <a className="underline" href="https://atomicedu.ca">atomicedu.ca</a>
        </p>
      </div>
    </div>
  );
};

export default SupplyPlanFreeDelivery;
